import {
  activity,
  article,
  category,
  channel,
  comment,
  initialize,
  like,
  notification,
  project,
  space,
  title,
  topic,
  user,
  content,
} from './spica/bucket';
import { localStorageService, STORAGE_KEY } from './local-storage.service';
import { IQueryParams, TActivity, TArticle, TCategory, TChannel, TComment, TContent, TLike, TNotification, TProject, TSpace, TTitle, TTopic, TUser } from '../models/common';
import authService from './auth.service';

interface IDatabase {
  updateUser(id: string, user_data: TUser): Promise<TUser>;
}

class DatabaseService implements IDatabase {
  publicUrl = process.env.REACT_APP_PUBLIC_URL;
  publicKey = process.env.REACT_APP_PUBLIC_APIKEY;

  projectId = localStorageService.getItem(STORAGE_KEY.PROJECT_ID) || undefined;

  initializeBucket() {
    const token = localStorageService.getItem(STORAGE_KEY.TOKEN);

    if (!token) {
      initialize({ apikey: this.publicKey!, publicUrl: this.publicUrl });
      return;
    }

    initialize({ identity: token!, publicUrl: this.publicUrl });
  }

  private fetchOne(resource: any, id: string, queryParams?: IQueryParams) {
    this.initializeBucket();
    return resource.get(id, { queryParams });
  }

  private fetchAll(resource: any, queryParams?: IQueryParams) {
    this.initializeBucket();
    return resource.getAll({ queryParams }).catch(this.handleErr);
  }

  private updateResource<T>(resource: any, id: string, data: T) {
    this.initializeBucket();
    return resource.patch({ _id: id, ...data }).catch(this.handleErr);
  }

  private deleteResource(resource: any, id: string) {
    this.initializeBucket();
    return resource.remove(id).catch(this.handleErr);
  }

  private insertResource<T>(resource: any, data: T) {
    this.initializeBucket();
    return resource.insert({ ...data, project: this.projectId }).catch(this.handleErr);
  }

  handleErr(err: { statusCode: number, message: string, error: string }) {
    if (err.statusCode === 401) {
      authService.logOut();
      window.location.href = "/login?hasError=true"
    }

    throw err;
  }

  getUser(id: string, queryParams?: IQueryParams): Promise<TUser> {
    return this.fetchOne(user, id, queryParams);
  }

  getUsers(queryParams?: IQueryParams): Promise<TUser[]> {
    return this.fetchAll(user, queryParams);
  }

  updateUser(id: string, data: TUser): Promise<TUser> {
    return this.updateResource(user, id, data);
  }

  getCategories(queryParams?: IQueryParams): Promise<TCategory[]> {
    return this.fetchAll(category, queryParams);
  }

  getChannels(queryParams?: IQueryParams): Promise<TChannel[]> {
    return this.fetchAll(channel, queryParams);
  }

  getTitles(queryParams?: IQueryParams): Promise<TTitle[]> {
    return this.fetchAll(title, queryParams);
  }

  getArticles(queryParams?: IQueryParams): Promise<TArticle[]> {
    return this.fetchAll(article, queryParams);
  }

  removeArticle(id: string): Promise<TArticle> {
    return this.deleteResource(article, id);
  }

  insertArticle(data: TArticle): Promise<TArticle> {
    return this.insertResource(article, data);
  }

  updateArticle(id: string, data: TArticle): Promise<TArticle> {
    return this.updateResource(article, id, data);
  }

  getLikes(queryParams?: IQueryParams): Promise<TLike[]> {
    return this.fetchAll(like, queryParams);
  }

  removeLike(id: string): Promise<TLike> {
    return this.deleteResource(like, id);
  }

  insertLike(data: TLike): Promise<TLike> {
    return this.insertResource(like, data);
  }

  getComments(queryParams?: IQueryParams): Promise<TComment[]> {
    return this.fetchAll(comment, queryParams);
  }

  removeComment(id: string): Promise<TComment> {
    return this.deleteResource(comment, id);
  }

  getTopics(queryParams?: IQueryParams): Promise<TTopic[]> {
    return this.fetchAll(topic, queryParams);
  }

  getSpaces(queryParams?: IQueryParams): Promise<TSpace[]> {
    return this.fetchAll(space, queryParams);
  }

  insertSpace(data: TSpace): Promise<TSpace> {
    return this.insertResource(space, data);
  }

  removeSpace(id: string): Promise<TSpace> {
    return this.deleteResource(space, id);
  }

  insertActivity(data: TActivity): Promise<TActivity> {
    return this.insertResource(activity, data);
  }

  getNotifications(queryParams?: IQueryParams): Promise<TNotification[]> {
    return this.fetchAll(notification, queryParams);
  }

  updateNotification(id: string, data: TNotification): Promise<TNotification> {
    return this.updateResource(notification, id, data);
  }

  getProject(): Promise<TProject> {
    const projectCode = process.env.REACT_APP_PROJECT_CODE;
    return this.fetchAll(project, {
      relation: true,
      filter: { project_code: projectCode },
    }).then((res: TProject[]) => res[0]);
  }

  getContent(queryParams?: IQueryParams): Promise<TContent[]> {
    return this.fetchAll(content, queryParams);
  }

  getNotificationsRealtime(queryParams?: IQueryParams) {
    return notification.realtime.getAll(queryParams);
  }
}

const dbService = new DatabaseService();
export default dbService;
